/** Sections 28-32 - stacks that arrive after the battle has started.
 *
 * `StackStore` is a struct of typed arrays sized once by `startBattle`, which
 * is what makes the in-place undo journal cheap. Summon Elemental and Clone
 * need a stack that was not there when the battle opened, and the two ways to
 * allow that are to reserve spare slots or to grow.
 *
 * **Growing is the one that costs nothing when nobody summons.** A reserve
 * would sit in every battle's state, be hashed by `canonicalize`, and have to
 * be skipped by every loop; growing replaces each array with a longer copy
 * through `jSetField`, which the journal records like any other write. Undo
 * replays newest-first, so writes made after the growth land on the long array
 * and the field-restore then swaps the short one back under the writes made
 * before it - the ordering works out, and `undoTo` needs no special case.
 *
 * It is O(n) per summon in a battle with at most a couple of dozen stacks, and
 * summons are rare. That is the whole trade.
 */

import type { CreatureDefId } from "../ids.js";
import type { Modifier } from "../modifiers.js";
import { type ContentRegistry, must } from "../content/registry.js";
import {
  BF_HEXES, F_ALIVE, F_TWO_HEX, SIDE_ATTACKER,
  type BattleState, type StackStore, hexCol, isAlive,
} from "./state.js";
import { F_SUMMONED } from "./flags.js";
import { jSetField, jSetI32 } from "./journal.js";
import {
  castsForCreature, flagsForCreature, shotsForCreature, strikesForCreature,
} from "./creature.js";
import { newOccupancy, canOccupy } from "./move.js";
import type { Ctx } from "./apply.js";
import { startCountModifier } from "./revenge.js";

/** Every Int32 column of the store, by name. Growing walks exactly this list. */
const I32_COLUMNS = [
  "side", "slotInArmy", "count", "topHp", "maxHp", "hex", "attack", "defense",
  "speed", "damageMin", "damageMax", "shots", "casts", "strikes", "morale", "luck",
  "flags", "flags2", "cooldown", "facing", "killed",
] as const;

function grownI32(src: Int32Array, extra: number): Int32Array {
  const out = new Int32Array(src.length + extra);
  out.set(src);
  return out;
}

/**
 * Make room for one more stack and return its slot. The slot is left with the
 * zero values a fresh column has; the caller fills it in and only then raises
 * `n`, so nothing reads a half-built stack.
 */
function growByOne(c: Ctx): number {
  const s = c.s;
  const st = s.stacks as unknown as Record<string, unknown>;
  const at = s.stacks.n;
  for (const k of I32_COLUMNS) {
    jSetField(c.j, st, k, grownI32(st[k] as Int32Array, 1));
  }
  jSetField(c.j, st, "def", [...s.stacks.def, s.stacks.def[0] ?? ("" as CreatureDefId)]);
  jSetField(c.j, st, "mods", [...s.stacks.mods, []]);
  // `order` and `waited` are per-slot too, and both live on the state rather
  // than on the store. A -1 at the end of the order reads as "the round is
  // over", which is what the tail of an order array already means, so the new
  // stack joins the running order at the next round rather than barging into
  // this one.
  jSetField(c.j, s as unknown as Record<string, unknown>, "order",
    (() => { const o = grownI32(s.order, 1); o[at] = -1; return o; })());
  jSetField(c.j, s as unknown as Record<string, unknown>, "waited", grownI32(s.waited, 1));
  return at;
}

export interface StackSpec {
  readonly def: CreatureDefId;
  readonly side: 0 | 1;
  readonly count: number;
  readonly hex: number;
  /** Extra flag bits, on top of the ones the creature's abilities imply. */
  readonly extraFlags?: number;
  /** Extra bits for the second flag word - a gated duplicate, and nothing else yet. */
  readonly extraFlags2?: number;
  readonly mods?: readonly Modifier[];
  /** Copied from the stack a Clone was made of; summons take the def's own. */
  readonly stats?: {
    readonly attack: number; readonly defense: number;
    readonly damageMin: number; readonly damageMax: number;
    readonly maxHp: number; readonly morale: number; readonly luck: number;
  };
}

/**
 * Put a new stack on the field. Returns its slot, or -1 when there is nowhere
 * to stand it - a summon with no room is a wasted cast, never a broken battle.
 */
export function addStack(c: Ctx, content: ContentRegistry, spec: StackSpec): number {
  if (spec.count <= 0 || spec.hex < 0 || spec.hex >= BF_HEXES) return -1;
  const def = content.creatures.get(spec.def);
  if (def === undefined) return -1;

  const slot = growByOne(c);
  const st = c.s.stacks;
  const flags = flagsForCreature(def) | F_ALIVE | F_SUMMONED | (spec.extraFlags ?? 0);
  const s = spec.stats;
  jSetField(c.j, st.def as unknown as Record<number, unknown>, slot, def.id);
  jSetI32(c.j, st.side, slot, spec.side);
  // -1: a summoned stack is not folded back into the army when the battle ends,
  // so it must not name a slot the fold would write casualties into.
  jSetI32(c.j, st.slotInArmy, slot, -1);
  jSetI32(c.j, st.count, slot, spec.count);
  jSetI32(c.j, st.maxHp, slot, s?.maxHp ?? def.hp);
  jSetI32(c.j, st.topHp, slot, s?.maxHp ?? def.hp);
  jSetI32(c.j, st.hex, slot, spec.hex);
  jSetI32(c.j, st.attack, slot, s?.attack ?? def.attack);
  jSetI32(c.j, st.defense, slot, s?.defense ?? def.defense);
  jSetI32(c.j, st.speed, slot, def.speed);
  jSetI32(c.j, st.damageMin, slot, s?.damageMin ?? def.damageMin);
  jSetI32(c.j, st.damageMax, slot, s?.damageMax ?? def.damageMax);
  jSetI32(c.j, st.shots, slot, shotsForCreature(def));
  jSetI32(c.j, st.casts, slot, castsForCreature(def));
  jSetI32(c.j, st.strikes, slot, strikesForCreature(def));
  jSetI32(c.j, st.morale, slot, s?.morale ?? 0);
  jSetI32(c.j, st.luck, slot, s?.luck ?? 0);
  jSetI32(c.j, st.flags, slot, flags);
  jSetI32(c.j, st.flags2, slot, spec.extraFlags2 ?? 0);
  jSetI32(c.j, st.cooldown, slot, 0);
  jSetI32(c.j, st.facing, slot, spec.side === SIDE_ATTACKER ? 1 : -1);
  jSetI32(c.j, st.killed, slot, 0);
  // Stamped like every stack that marched in: a summoned stack can be
  // resurrected too, and its ceiling is the count it appeared with.
  jSetField(c.j, st.mods as unknown as Record<number, unknown>, slot,
    [...(spec.mods ?? []), startCountModifier(spec.count)]);
  // Raised last: until `n` moves, nothing in the kernel can see the slot.
  jSetField(c.j, st as unknown as Record<string, unknown>, "n", slot + 1);
  void must(content.creatures, def.id, "creature");
  return slot;
}

/**
 * A free hex for a new stack, hunted outward from `prefer` and falling back to
 * the summoning side's own column. Deliberately a scan of the whole field in
 * hex order: it is called once per summon and a deterministic answer matters
 * more than a fast one.
 */
export function freeHexFor(
  s: BattleState, side: 0 | 1, prefer: number, twoHex: boolean,
): number {
  const occ = newOccupancy(s);
  const fits = (h: number): boolean => {
    if (occ[h]! >= 0) return false;
    if (!twoHex) return true;
    const tail = h - (side === SIDE_ATTACKER ? 1 : -1);
    return tail >= 0 && tail < BF_HEXES && occ[tail]! < 0 && hexCol(tail) === hexCol(h) - (side === SIDE_ATTACKER ? 1 : -1);
  };
  if (prefer >= 0 && prefer < BF_HEXES && fits(prefer)) return prefer;
  // Nearest first, so a Clone appears beside what it copied.
  const order: number[] = [];
  for (let h = 0; h < BF_HEXES; h++) order.push(h);
  if (prefer >= 0) {
    order.sort((a, b) => Math.abs(a - prefer) - Math.abs(b - prefer) || a - b);
  }
  for (const h of order) if (fits(h)) return h;
  return -1;
}

/** Where a two-hex stack's tail would go, for the occupancy check above. */
export const isTwoHexDef = (st: StackStore, slot: number): boolean =>
  (st.flags[slot]! & F_TWO_HEX) !== 0;

/** The slots a side still has standing, for "did the summon actually help". */
export function aliveOnSide(st: StackStore, side: number): number {
  let n = 0;
  for (let i = 0; i < st.n; i++) if (st.side[i] === side && isAlive(st, i)) n++;
  return n;
}

void canOccupy;
